import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Badge, Button, Card, Col, Form, Modal, Nav, Row, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import { useLoading } from '../contexts/LoadingContext'
import axiosInstance from '../services/axiosInterceptor'
import Swal from 'sweetalert2'
import { useAuth } from '../services/authProvider'
import { getTrackingRoleLabel, getLevelSuffix } from '../utils/roleLabels'
import { useReactToPrint } from 'react-to-print'
import CareContractDoc from './print/CareContractDoc'

// ติดตามการดูแลนักเรียน (หัวหน้าระดับ)
// นักเรียนที่เข้าเกณฑ์ของแต่ละแกน (เข้าเรียน / การเรียน / ความประพฤติ) จะมีขั้นตอนดูแล
// ตามระดับความเสี่ยงเป็นบันไดขั้น — ครูประจำชั้นดูแลก่อนที่ระดับเฝ้าระวัง/เสี่ยง แล้วหัวหน้าระดับ
// เข้ามาที่ระดับเสี่ยงสูง หน้านี้เป็น checklist: ติ๊กถูกเมื่อดำเนินการแล้ว ไม่ต้องพิมพ์อะไร
// (หมายเหตุสั้น ๆ ใส่เพิ่มได้จากไอคอนโน้ต) รายการนักเรียนดึงจาก risk list เดียวกับ
// หน้าระบบติดตามนักเรียน (Early Warning) จึงใช้เกณฑ์ตัวเลขชุดเดียวกัน

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '')
const FILTERS_KEY = 'careTracking.filters'

const escHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const RISK_META = {
  red:    { bg: 'danger',  label: 'เสี่ยงสูง' },
  orange: { bg: 'orange',  label: 'เสี่ยง' },
  yellow: { bg: 'warning', label: 'เฝ้าระวัง' },
  green:  { bg: 'success', label: 'ปกติ' },
}

// ระดับ (ต่ำ → สูง) — ชุดเดียวกับ LEVEL_POINTS ใน careActionModel
const LEVEL_POINTS = { green: 0, yellow: 1, orange: 2, red: 3 }
const aboveWatch = (lv) => (LEVEL_POINTS[lv] || 0) > LEVEL_POINTS.yellow

// แท็บ = แกนความเสี่ยง — คำอธิบายเกณฑ์ใช้ค่าเดียวกับ trackingModel.RISK
const AXIS_TABS = [
  { key: 'attendance', label: 'การเข้าเรียน', icon: 'ri-calendar-close-line',
    criteria: 'เฝ้าระวัง ขาด 10–20% · เสี่ยง 20–40% · เสี่ยงสูง มากกว่า 40%' },
  { key: 'academic',   label: 'การส่งงาน/สอบ', icon: 'ri-book-2-line',
    criteria: 'เฝ้าระวัง อ่อน 1 วิชา หรือค้างส่งงาน · เสี่ยงสูง อ่อน 2 วิชาขึ้นไป หรือค้างส่ง 5 งานขึ้นไป' },
  { key: 'behavior',   label: 'ความประพฤติ',  icon: 'ri-shield-user-line',
    criteria: 'เฝ้าระวัง คะแนนความประพฤติต่ำกว่า 90 · เสี่ยงสูง ต่ำกว่า 70' },
]

// ป้ายหัวตาราง — สื่อว่าขั้นนั้นต้องทำอะไร แทนคำว่าระดับความเสี่ยง
const STEP_HEAD_LABEL = {
  yellow: 'พูดคุย/ตักเตือน',
  orange: 'โทรแจ้งผู้ปกครอง/ทำสัญญา',
}

// หน้านี้ยิงคิวรีที่หนักที่สุดของระบบ ถ้าคนเปิดพร้อมกันเยอะจนคิวฝั่งเซิร์ฟเวอร์เต็ม
// จะได้ 503 กลับมา ซึ่งไม่ใช่ระบบพัง — บอกให้รอสักครู่แล้วกดลองใหม่ได้เลยดีกว่า
// เด้ง "โหลดข้อมูลไม่สำเร็จ" ที่ครูทำอะไรต่อไม่ถูก (ฝั่ง server ดู utils/dbBusy.js)
const alertRequestError = (err, fallbackTitle, { retry = false } = {}) => {
  const message = err?.response?.data?.message
  if (err?.response?.status === 503) {
    return Swal.fire({
      icon: 'info',
      title: 'ระบบกำลังหนาแน่น',
      text: message || 'ตอนนี้มีผู้ใช้งานหน้านี้พร้อมกันหลายคน กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
      showCancelButton: retry,
      confirmButtonText: retry ? 'ลองใหม่อีกครั้ง' : 'เข้าใจแล้ว',
      cancelButtonText: 'ไว้ก่อน',
    })
  }
  return Swal.fire(fallbackTitle, message || 'กรุณาลองใหม่อีกครั้ง', 'error')
}

const WHO_META = {
  advisor: { label: 'ครูประจำชั้น', bg: 'info' },
  head:    { label: 'หัวหน้าระดับ', bg: 'primary' },
}

const StudentAvatar = ({ photo, name, size = 32 }) => {
  const initial = name ? name.trim().charAt(0) : '?'
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null
  const style = { width: size, height: size, borderRadius: '50%', flexShrink: 0 }
  return src
    ? <img src={src} alt="" style={{ ...style, objectFit: 'cover' }} />
    : <span style={{ ...style, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>
}

const LevelBadge = ({ level, children }) => {
  const meta = RISK_META[level] || RISK_META.green
  const darkText = meta.bg === 'warning' || meta.bg === 'orange'
  return <Badge bg={meta.bg} text={darkText ? 'dark' : undefined} className="fs-xs">{children || meta.label}</Badge>
}

// เบอร์โทรผู้ปกครอง — โชว์เฉพาะแถวที่เลยระดับเฝ้าระวัง เพราะขั้นถัดไปของนักเรียน
// กลุ่มนี้คือโทรแจ้งผู้ปกครอง (server ส่ง contact มาให้เฉพาะแถวเหล่านั้น)
const ContactPhone = ({ contact, level }) => {
  if (!contact?.phone) {
    return <span className="fs-xs text-muted">{aboveWatch(level) ? 'ไม่มีเบอร์' : '—'}</span>
  }
  const who = [contact.name, contact.relation].filter(Boolean).join(' · ')
  return (
    <>
      <a href={`tel:${contact.phone.replace(/[^0-9+]/g, '')}`} className="fs-sm text-nowrap">
        <i className="ri-phone-line me-1" />{contact.phone}
      </a>
      {who && <div className="fs-xs text-muted text-truncate" title={who}>{who}</div>}
    </>
  )
}

// ตัวเลขที่ทำให้นักเรียนเข้าเกณฑ์ของแกนนั้น — แสดงใต้ป้ายระดับ
const axisDetail = (axisKey, a) => {
  if (!a) return ''
  if (axisKey === 'attendance') return `ขาด ${a.absent} ครั้ง (${a.absenceRate}%) · สาย ${a.late} · ลา ${a.leave}`
  if (axisKey === 'academic')   return `อ่อน ${a.weakSubjects} วิชา · ค้างส่ง ${a.missingItems} งาน`
  if (axisKey === 'behavior')   return `เหลือ ${a.remaining} คะแนน (หัก ${a.deducted}${Number(a.merited || 0) > 0 ? ` · คืน ${a.merited}` : ''})`
  return ''
}

const fmtDate = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d) ? '' : d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })
}

// ความคืบหน้าหลังติ๊ก — server เก็บตัวเลขของแกน ณ ตอนติ๊กไว้ แล้วเทียบกับตัวเลขปัจจุบัน
// (attendance = % ขาด, academic = วิชาอ่อน+งานค้าง, behavior = คะแนนที่เหลือ)
// ลูกศรเขียวชี้ขึ้น = ดีขึ้น, ลูกศรแดงชี้ลง = แย่ลง, ขีด = เท่าเดิม
const TREND_META = {
  better: { icon: 'ri-arrow-up-line',   cls: 'text-success', label: 'ดีขึ้น' },
  worse:  { icon: 'ri-arrow-down-line', cls: 'text-danger',  label: 'แย่ลง' },
  same:   { icon: 'ri-subtract-line',   cls: 'text-muted',   label: 'เท่าเดิม' },
}
const fmtMetric = (v, unit) => `${v}${unit === '%' ? '%' : ''}`

const ProgressHint = ({ progress }) => {
  if (!progress) return null
  const t = TREND_META[progress.trend] || TREND_META.same
  return (
    <div className={`fs-xs text-nowrap lh-1 mt-1 ${t.cls}`} title={`${t.label} — ตอนติ๊ก ${fmtMetric(progress.before, progress.unit)} → ตอนนี้ ${fmtMetric(progress.now, progress.unit)}`}>
      <i className={`${t.icon} fw-bold`} />
      {fmtMetric(progress.before, progress.unit)} → {fmtMetric(progress.now, progress.unit)}
    </div>
  )
}

// บรรทัด "ความคืบหน้า" ใน tooltip ของช่องติ๊ก
const progressText = (axisKey, progress) => {
  if (!progress) return ''
  const t = TREND_META[progress.trend] || TREND_META.same
  const before = axisDetail(axisKey, progress.snapshot) || fmtMetric(progress.before, progress.unit)
  return `\nตอนติ๊ก: ${before}\nตอนนี้: ${fmtMetric(progress.now, progress.unit)} (${t.label}${progress.delta ? ` ${progress.delta > 0 ? '+' : ''}${progress.delta}${progress.unit === '%' ? '%' : ''}` : ''})`
}

// ยกเลิกติ๊กแล้วติ๊กใหม่ = ลบแถวแล้วเขียนใหม่ ตัวเลขตอนติ๊ก/ผู้บันทึก/วันที่จึงกลายเป็นของ
// วันที่ติ๊กใหม่ ช่องที่ตัวเลขขยับไปจากตอนติ๊กแล้วจึงล็อกไว้ — server เป็นคนตัดสิน (ส่งมาใน
// action.locked) และปฏิเสธซ้ำอีกชั้นตอน POST ตรงนี้แค่ปิดช่องไม่ให้กดเปล่าๆ
const LOCK_TIP = 'ยกเลิกติ๊กไม่ได้ — ตัวเลขเปลี่ยนไปจากตอนติ๊กแล้ว ติ๊กใหม่จะได้ตัวเลขของวันนี้แทน (ถ้าติ๊กผิดให้แจ้งผู้ดูแลระบบ)'

// ช่องติ๊กหนึ่งขั้นตอนของนักเรียนหนึ่งคน
//  - ติ๊กแล้ว: tooltip ใคร/เมื่อไร
//  - ยังไม่ติ๊กแต่ต้องทำ (ระดับถึงขั้นนี้): ปกติ
//  - ยังไม่ถึงขั้นนี้: ไม่มีช่องติ๊ก แสดง "ยังไม่ถึง" แทน — กันติ๊กล่วงหน้า
//    (ถ้าเคยติ๊กตอนที่เข้าเกณฑ์ แล้วระดับลดลง ยังเห็นเครื่องหมายถูกและแก้ไขได้)
//  - `allowed` = false: ผู้ใช้คนนี้ไม่มีสิทธิ์ติ๊กขั้นนี้ (ขั้นครูประจำชั้นติ๊กได้เฉพาะ
//    ครูประจำชั้นห้องนั้น ขั้นหัวหน้าระดับติ๊กได้เฉพาะหัวหน้าระดับ) → ดูได้อย่างเดียว
//  - ใต้ช่องที่ติ๊กแล้วมีลูกศรบอกความคืบหน้า (ตัวเลขตอนติ๊ก → ตอนนี้) ถ้า server มี snapshot
const StepCheck = ({ axisKey, step, action, required, allowed, busy, onToggle, onNote }) => {
  const done = !!action
  // คนที่ติ๊กไม่ได้อยู่แล้ว (ดูอย่างเดียว) ไม่ต้องรู้เรื่องล็อก ช่องปิดอยู่แล้วด้วย allowed
  const locked = done && allowed && !!action.locked
  const whoLabel = WHO_META[step.who]?.label || ''
  const tip = done
    ? `${action.done_by_name || 'ไม่ระบุผู้บันทึก'} · ${fmtDate(action.done_at)}${action.note ? `\n${action.note}` : ''}${progressText(axisKey, action.progress)}${locked ? `\n${LOCK_TIP}` : ''}`
    : !allowed ? `ยังไม่ได้ดำเนินการ — ติ๊กได้เฉพาะ${whoLabel}${step.who === 'advisor' ? 'ของห้องนี้' : ''}`
    : `ยังไม่ได้ดำเนินการ: ${step.label}`

  // ยังไม่ถึงระดับของขั้นนี้ และยังไม่เคยติ๊ก — ไม่มีช่องติ๊ก บอกว่ายังไม่ถึงแทน
  if (!done && !required) {
    return (
      <OverlayTrigger placement="top" overlay={
        <Tooltip>{`ยังไม่ถึงระดับ${RISK_META[step.level].label} — ยังไม่ต้องดำเนินการขั้นนี้`}</Tooltip>
      }>
        <span className="fs-xs text-muted fst-italic" onClick={e => e.stopPropagation()}>ยังไม่ถึง</span>
      </OverlayTrigger>
    )
  }

  return (
    <div className="d-inline-flex flex-column align-items-center" onClick={e => e.stopPropagation()}>
      <div className="d-inline-flex align-items-center gap-1">
        <OverlayTrigger placement="top" overlay={<Tooltip style={{ whiteSpace: 'pre-line' }}>{tip}</Tooltip>}>
          <span className="d-inline-block">
            <Form.Check
              type="checkbox"
              className="m-0"
              checked={done}
              disabled={busy || !allowed || locked}
              onChange={() => onToggle(!done)}
              style={{ transform: 'scale(1.35)', cursor: busy ? 'wait' : (allowed && !locked) ? 'pointer' : 'not-allowed' }}
            />
          </span>
        </OverlayTrigger>
        {done && allowed && (
          <button
            type="button"
            className={`btn btn-link btn-sm p-0 lh-1 ${action.note ? 'text-primary' : 'text-muted'}`}
            title={action.note ? action.note : 'เพิ่มหมายเหตุ (ไม่บังคับ)'}
            onClick={onNote}
          >
            <i className={action.note ? 'ri-sticky-note-fill' : 'ri-sticky-note-line'} />
          </button>
        )}
        {locked && <i className="ri-lock-line fs-xs text-muted" title={LOCK_TIP} />}
      </div>
      {done && <ProgressHint progress={action.progress} />}
    </div>
  )
}

const getSavedFilters = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTERS_KEY) || '{}') } catch { return {} }
}

export default function CareTracking() {
  const { setLoading } = useLoading()
  const { posIds } = useAuth()
  const navigate = useNavigate()
  const roleLabel = getTrackingRoleLabel(posIds)
  const levelSuffix = getLevelSuffix(posIds)
  // หัวหน้าวิชาการ (3/4) ดูสถานะได้ทั้งโรงเรียนแต่ติ๊กไม่ได้ — server คืน canTick ว่าง
  // ให้อยู่แล้ว ตรงนี้แค่เปลี่ยนชื่อหน้า/คำอธิบายให้ตรงกับเมนู "สถานะติดตามการดูแลนักเรียน"
  const readOnly = !(Array.isArray(posIds) && posIds.some(p => [2, 21, 22, 99].includes(p)))
  const pageTitle = readOnly ? 'สถานะติดตามการดูแลนักเรียน' : 'ติดตามการดูแลนักเรียน'

  // Dark-mode tables: mirror the other report pages — toggle `table-dark` on
  // every <thead> whenever the skin is dark (runs each render so tables that
  // mount after data loads get styled too).
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : ''
  const [skin, setSkin] = useState(currentSkin)
  const switchSkin = (s) => {
    for (const t of document.getElementsByTagName('table')) {
      t.querySelectorAll('thead').forEach(th =>
        s === 'dark' ? th.classList.add('table-dark') : th.classList.remove('table-dark'))
    }
  }
  useEffect(() => { switchSkin(skin) })

  const [yearId, setYearId] = useState(null)
  const [yearLabel, setYearLabel] = useState('')
  const [classList, setClassList] = useState([])
  const [roomList, setRoomList] = useState([])

  const [axis, setAxis] = useState(() => getSavedFilters().axis ?? 'attendance')
  const [semester, setSemester] = useState(() => getSavedFilters().semester ?? 'all')
  const [classId, setClassId] = useState(() => getSavedFilters().classId ?? '')
  const [roomId, setRoomId] = useState(() => getSavedFilters().roomId ?? '')
  const [gender, setGender] = useState(() => getSavedFilters().gender ?? '')
  const [level, setLevel] = useState(() => getSavedFilters().level ?? '')        // '' | red | orange | yellow
  const [status, setStatus] = useState(() => getSavedFilters().status ?? 'pending') // pending | complete | ''

  const [steps, setSteps] = useState([])
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({ total: 0, pending: 0, complete: 0, byLevel: {} })
  const [pendingByAxis, setPendingByAxis] = useState({}) // ตัวเลขบนแท็บ
  const [busyKey, setBusyKey] = useState(null) // `${st_id}:${step}` ที่กำลังบันทึก
  const [contract, setContract] = useState(null) // ข้อมูลหนังสือสัญญาที่กำลังเปิดดู/พิมพ์
  const contractRef = useRef(null)

  const axisMeta = AXIS_TABS.find(t => t.key === axis) || AXIS_TABS[0]

  useEffect(() => {
    axiosInstance.get('/api/getLastestStudyYear')
      .then(res => {
        if (res.data?.year_id) setYearId(res.data.year_id)
        if (res.data?.year_year) setYearLabel(String(res.data.year_year))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!yearId) return
    axiosInstance.get(`/api/tracking/filterOptions/${yearId}`)
      .then(res => {
        setClassList(res.data?.classes || [])
        setRoomList(res.data?.rooms || [])
      })
      .catch(() => {})
  }, [yearId])

  // ตัวกรองที่จำไว้อาจชี้ไปชั้น/ห้องนอกสิทธิ์ — ล้างทิ้ง
  useEffect(() => {
    if (classList.length && classId && !classList.some(c => String(c.c_id) === String(classId))) setClassId('')
  }, [classList, classId])
  useEffect(() => {
    if (roomList.length && roomId && !roomList.some(r => String(r.r_id) === String(roomId))) setRoomId('')
  }, [roomList, roomId])
  useEffect(() => {
    if (!gender || !roomId || !roomList.length) return
    const r = roomList.find(x => String(x.r_id) === String(roomId))
    if (r && r.r_gen !== gender) setRoomId('')
  }, [gender, roomId, roomList])
  const isFirstClassRender = useRef(true)
  useEffect(() => {
    if (isFirstClassRender.current) { isFirstClassRender.current = false; return }
    setRoomId('')
  }, [classId])

  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ axis, semester, classId, roomId, gender, level, status }))
  }, [axis, semester, classId, roomId, gender, level, status])

  // ตัวกรองร่วม (ไม่รวมแกน/ระดับ/สถานะ) — ใช้ทั้งตารางและตัวเลขบนแท็บ
  const scopeQuery = useMemo(() => {
    const p = new URLSearchParams()
    if (semester !== 'all') p.append('semester', semester)
    if (classId) p.append('classId', classId)
    if (roomId) p.append('roomId', roomId)
    if (gender) p.append('gender', gender)
    return p.toString()
  }, [semester, classId, roomId, gender])

  // ปุ่ม "ลองใหม่อีกครั้ง" ตอนคิวเต็มต้องเรียก fetchData ตัวล่าสุด จึงถือผ่าน ref
  // (เรียกตัวเองใน useCallback ตรงๆ ไม่ได้ และไม่อยากให้ทั้งหน้า re-render เพื่อการนี้)
  const fetchDataRef = useRef(null)

  // คำขอเดียวได้ทั้งตารางของแกนที่เลือกและตัวเลข "ยังไม่ครบ" ของทุกแท็บ
  // (เดิมยิงแยกแกนละคำขอ = 4 คำขอต่อการเปิดหน้า/เปลี่ยนตัวกรอง 1 ครั้ง ทั้งที่คิวรี
  // ที่หนักจริงเป็นตัวเดียวกันหมด)
  const fetchData = useCallback(() => {
    if (!yearId) return
    setLoading(true)
    const p = new URLSearchParams(scopeQuery)
    p.append('axis', axis)
    if (level) p.append('level', level)
    if (status) p.append('status', status)
    axiosInstance.get(`/api/tracking/care/${yearId}?${p.toString()}`)
      .then(res => {
        setSteps(res.data?.steps || [])
        setRows(res.data?.rows || [])
        setSummary(res.data?.summary || { total: 0, pending: 0, complete: 0, byLevel: {} })
        setPendingByAxis(res.data?.pendingByAxis || {})
      })
      .catch(err => alertRequestError(err, 'โหลดข้อมูลไม่สำเร็จ', { retry: true })
        .then(result => { if (result.isConfirmed && err?.response?.status === 503) fetchDataRef.current?.() }))
      .finally(() => setLoading(false))
  }, [yearId, scopeQuery, axis, level, status, setLoading])

  useEffect(() => { fetchDataRef.current = fetchData; fetchData() }, [fetchData])

  const filteredRooms = useMemo(
    () => roomList.filter(r =>
      (!classId || r.c_id === parseInt(classId, 10)) &&
      (!gender || r.r_gen === gender)),
    [roomList, classId, gender]
  )

  // อัปเดตแถวในหน้าโดยไม่โหลดใหม่ทั้งตาราง — คำนวณ missing/complete ใหม่จาก required
  const applyAction = useCallback((stId, stepKey, action) => {
    setRows(prev => prev.map(r => {
      if (r.st_id !== stId) return r
      const actions = { ...r.actions }
      if (action) actions[stepKey] = action; else delete actions[stepKey]
      const missing = r.required.filter(k => !actions[k])
      return { ...r, actions, missing, complete: missing.length === 0 }
    }))
  }, [])

  // ติ๊ก 1 ครั้งกระทบตัวเลขบนแท็บได้แค่แกนที่กำลังดูอยู่ และเปลี่ยนได้ทีละ 1 คน
  // จึงบวก/ลบเองได้ ไม่ต้องยิงคิวรีทั้งโรงเรียนใหม่เพียงเพื่ออัปเดตเลขบนแท็บ
  const bumpPending = useCallback((row, stepKey, action) => {
    const next = { ...(row.actions || {}) }
    if (action) next[stepKey] = action; else delete next[stepKey]
    const completeAfter = (row.required || []).filter(k => !next[k]).length === 0
    if (Boolean(row.complete) === completeAfter) return
    setPendingByAxis(prev => ({
      ...prev,
      [axis]: Math.max(0, Number(prev[axis] || 0) + (completeAfter ? -1 : 1)),
    }))
  }, [axis])

  const toggleStep = async (row, step, done) => {
    if (!yearId) return
    const key = `${row.st_id}:${step.key}`
    setBusyKey(key)
    try {
      // ส่งภาคเรียนไปด้วย ให้ตัวเลขที่ server เก็บ ณ ตอนติ๊ก ตรงกับที่ตารางแสดงอยู่
      const res = await axiosInstance.post(`/api/tracking/care/${yearId}/${row.st_id}`, {
        axis, step: step.key, done, level: row.axis?.level, semester,
      })
      applyAction(row.st_id, step.key, res.data?.action || null)
      bumpPending(row, step.key, res.data?.action || null)
    } catch (err) {
      alertRequestError(err, 'บันทึกไม่สำเร็จ')
    } finally {
      setBusyKey(null)
    }
  }

  const editNote = async (row, step) => {
    const current = row.actions?.[step.key]?.note || ''
    const { value, isConfirmed } = await Swal.fire({
      title: step.short,
      text: `${row.st_name} · หมายเหตุสั้น ๆ (ไม่บังคับ)`,
      input: 'text',
      inputValue: current,
      inputAttributes: { maxlength: 255 },
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
    })
    if (!isConfirmed) return
    try {
      const res = await axiosInstance.post(`/api/tracking/care/${yearId}/${row.st_id}`, {
        axis, step: step.key, done: true, level: row.axis?.level, note: value || '', semester,
      })
      applyAction(row.st_id, step.key, res.data?.action || null)
      bumpPending(row, step.key, res.data?.action || null)
    } catch (err) {
      alertRequestError(err, 'บันทึกไม่สำเร็จ')
    }
  }

  // พิมพ์หนังสือสัญญา — ดึงข้อมูลนักเรียน (ชั้น หอพัก ผู้ปกครอง) มาเติมลงแบบฟอร์ม
  // แล้วเปิดตัวอย่างให้ตรวจก่อนสั่งพิมพ์ ช่องที่ระบบไม่มีข้อมูลจะพิมพ์เป็นเส้นประให้เขียนมือ
  const openContract = async (row) => {
    setLoading(true)
    try {
      const res = await axiosInstance.get(`/api/tracking/care/contract/${yearId}/${row.st_id}`)
      setContract(res.data)
    } catch (err) {
      alertRequestError(err, 'เปิดหนังสือสัญญาไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  // ขนาดกระดาษต้องส่งผ่าน pageStyle ไม่ใช่ @page ในไฟล์ CSS เพราะ CRA รวม CSS ทั้งแอป
  // เป็นสไตล์ชีตเดียว กฎ @page จึงหลุดไปทับหน้าอื่นทั้งระบบ
  const printContract = useReactToPrint({
    contentRef: contractRef,
    documentTitle: contract
      ? `หนังสือสัญญา ${contract.st_idnumber || ''} ${contract.st_name || ''}`.trim()
      : 'หนังสือสัญญา',
    pageStyle: '@page { size: A4 portrait; margin: 0; }',
  })

  // พิมพ์ตารางติดตามการดูแลตามตัวกรองปัจจุบัน — A4 แนวนอน เปิดหน้าต่างใหม่แล้วสั่งพิมพ์
  // (แบบเดียวกับตารางจัดอันดับในหน้าระบบติดตามนักเรียน) ขั้นที่ยังไม่ได้ติ๊กพิมพ์เป็น ☐
  // ไว้ให้ติ๊กด้วยมือในที่ประชุม ส่วนขั้นที่ยังไม่ถึงระดับพิมพ์เป็น - ไม่ใช่ช่องว่าง เพื่อไม่ให้
  // เข้าใจผิดว่าลืมทำ
  const printChecklist = () => {
    if (!rows.length) return
    const classLabel = classId
      ? (classList.find(c => String(c.c_id) === String(classId))?.c_class || `ม.${classId}`)
      : 'ทุกชั้น'
    const room = roomId ? roomList.find(r => String(r.r_id) === String(roomId)) : null
    const conditions = [
      { m: 'นักเรียนชาย', f: 'นักเรียนหญิง' }[gender] || 'ทุกเพศ',
      classLabel,
      room ? `ห้อง ${room.c_id}/${room.r_no}` : 'ทุกห้อง',
      axis === 'academic' ? (semester === 'all' ? 'ทั้งปี' : `ภาคเรียนที่ ${semester}`) : null,
      level ? RISK_META[level].label : 'ทุกระดับ',
      { pending: 'ยังไม่ครบ', complete: 'ครบแล้ว' }[status] || 'ทุกสถานะ',
    ].filter(Boolean).join(' | ')

    const stepCell = (r, s) => {
      const a = r.actions?.[s.key]
      if (!a) return r.required.includes(s.key) ? '<span class="box">☐</span>' : '-'
      const sub = [
        fmtDate(a.done_at),
        a.progress ? `${fmtMetric(a.progress.before, a.progress.unit)} → ${fmtMetric(a.progress.now, a.progress.unit)}` : null,
      ].filter(Boolean).join(' · ')
      return `<span class="tick">✔</span> ${escHtml(a.done_by_name || '')}`
        + `<div class="sub">${escHtml(sub)}</div>`
        + (a.note ? `<div class="sub">${escHtml(a.note)}</div>` : '')
    }

    const contactCell = (c) => (c?.phone
      ? `${escHtml(c.phone)}<div class="sub">${escHtml([c.name, c.relation].filter(Boolean).join(' · '))}</div>`
      : '-')

    const body = rows.map((r, idx) => `<tr>
        <td class="c">${idx + 1}</td>
        <td class="c">${escHtml(r.st_idnumber)}</td>
        <td>${escHtml(r.st_name)}</td>
        <td class="c">${escHtml(r.class_room)}</td>
        <td class="c">${escHtml(r.religious_room || '-')}</td>
        <td class="c">${escHtml(RISK_META[r.axis?.level]?.label || '-')}</td>
        <td>${escHtml(axisDetail(axis, r.axis))}</td>
        <td class="c">${contactCell(r.contact)}</td>
        ${steps.map(s => `<td class="c">${stepCell(r, s)}</td>`).join('')}
        <td class="c">${r.complete ? 'ครบ' : `ขาด ${r.missing.length}/${r.required.length}`}</td>
      </tr>`).join('')

    // คอลัมน์ขั้นตอนมี 2–3 ช่องแล้วแต่แกน จึงแบ่งความกว้างที่เหลือให้เท่าๆ กัน
    const stepWidth = (27 / Math.max(steps.length, 1)).toFixed(2)
    const cols = ['4', '8', '16', '6', '6', '6', '12', '9', ...steps.map(() => stepWidth), '6']
    const title = `ติดตามการดูแลนักเรียน — ${axisMeta.label}${yearLabel ? ` ปีการศึกษา ${yearLabel}` : ''}`
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>${escHtml(title)}</title>
      <style>
        @page { size: A4 landscape; margin: 8mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: 'Cordia New', 'TH Sarabun New', Tahoma, Arial, sans-serif; font-size: 14px; color: #000; }
        h3, h4 { text-align: center; margin: 0 0 4px 0; }
        h3 { font-size: 18px; }
        h4 { font-size: 15px; font-weight: normal; }
        .meta { font-size: 12px; margin: 6px 0; display: flex; justify-content: space-between; }
        .legend { font-size: 12px; margin-bottom: 4px; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        th, td { border: 1px solid #000; padding: 2px 3px; overflow: hidden; word-wrap: break-word; }
        thead { display: table-header-group; }
        thead th { text-align: center; font-size: 12px; background: #f0f0f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tbody tr { page-break-inside: avoid; }
        .c { text-align: center; }
        .sub { font-size: 11px; color: #444; }
        .tick { font-weight: bold; }
        .box { font-size: 16px; }
        .sign { margin-top: 14px; font-size: 12px; text-align: right; }
      </style></head>
      <body onload="window.print()">
        <h3>โรงเรียนวัฒนธรรมอิสลาม</h3>
        <h4>${escHtml(title)}</h4>
        <div class="meta">
          <span>เงื่อนไข: ${escHtml(conditions)}</span>
          <span>รวม ${rows.length} คน (ครบ ${summary.complete || 0} / ยังไม่ครบ ${summary.pending || 0})</span>
        </div>
        <div class="legend">✔ ดำเนินการแล้ว · ☐ ยังไม่ดำเนินการ · - ยังไม่ถึงระดับของขั้นนี้</div>
        <table>
          <colgroup>${cols.map(w => `<col style="width:${w}%;" />`).join('')}</colgroup>
          <thead><tr>
            <th>#</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ห้องสามัญ</th><th>ห้องศาสนา</th>
            <th>ระดับ</th><th>ข้อมูล</th><th>เบอร์ผู้ปกครอง</th>
            ${steps.map(s => `<th>${escHtml(s.short)}<div class="sub">${escHtml(STEP_HEAD_LABEL[s.level] || '')} · ${escHtml(WHO_META[s.who]?.label || '')}</div></th>`).join('')}
            <th>สถานะ</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
        <div class="sign">พิมพ์เมื่อ ${new Date().toLocaleString('th-TH')}</div>
      </body></html>`

    const win = window.open('', '_blank', 'width=1200,height=900')
    if (!win) return Swal.fire('พิมพ์ไม่ได้', 'กรุณาอนุญาต popup ของเบราว์เซอร์', 'warning')
    win.document.write(html)
    win.document.close()
  }

  // เมื่อกรอง "ยังไม่ครบ" อยู่ แถวที่ติ๊กครบแล้วยังคงอยู่จนกว่าจะโหลดใหม่ — ตั้งใจให้เป็นแบบนี้
  // เพื่อไม่ให้แถวหายไปทันทีที่ติ๊ก (ติ๊กผิดยังแก้ได้)
  const summaryCards = [
    { key: 'pending',  label: 'ยังดำเนินการไม่ครบ', value: summary.pending,  bg: 'danger',  icon: 'ri-error-warning-line' },
    { key: 'complete', label: 'ดำเนินการครบแล้ว',   value: summary.complete, bg: 'success', icon: 'ri-checkbox-circle-line' },
    { key: 'red',      label: 'เสี่ยงสูง',            value: summary.byLevel?.red,    bg: 'danger',  icon: 'ri-alarm-warning-line', filter: 'red' },
    { key: 'orange',   label: 'เสี่ยง',                value: summary.byLevel?.orange, bg: 'orange',  icon: 'ri-error-warning-line', filter: 'orange', hide: !steps.some(s => s.level === 'orange') },
    { key: 'yellow',   label: 'เฝ้าระวัง',            value: summary.byLevel?.yellow, bg: 'warning', icon: 'ri-eye-line', filter: 'yellow' },
  ].filter(c => !c.hide)

  const levelOptions = [['', 'ทุกระดับ'], ...['red', 'orange', 'yellow']
    .filter(l => steps.some(s => s.level === l))
    .map(l => [l, RISK_META[l].label])]

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">{roleLabel}</Link></li>
              <li className="breadcrumb-item active">{pageTitle}</li>
            </ol>
            <h4 className="main-title mb-0">
              <i className="ri-checkbox-multiple-line me-2 text-primary" />
              {pageTitle}{levelSuffix}{yearLabel && <span className="text-muted fs-5 fw-normal"> ปีการศึกษา {yearLabel}</span>}
            </h4>
            <p className="text-muted fs-sm mb-0">
              {readOnly
                ? 'ดูสถานะการดำเนินการของครูประจำชั้นและหัวหน้าระดับ (ดูอย่างเดียว) — ช่องที่ติ๊กแล้วชี้เพื่อดูว่าใครบันทึก เมื่อไร'
                : 'ติ๊กถูกเมื่อดำเนินการตามขั้นตอนแล้ว — ขั้นของครูประจำชั้นติ๊กได้เฉพาะครูประจำชั้นห้องนั้น ขั้นของหัวหน้าระดับติ๊กได้เฉพาะหัวหน้าระดับ'}
            </p>
          </div>
        </div>

        {/* แท็บแกนความเสี่ยง */}
        <Nav variant="pills" className="nav-sm gap-1 mb-3 flex-wrap">
          {AXIS_TABS.map(t => (
            <Nav.Item key={t.key}>
              <Nav.Link active={axis === t.key} onClick={() => { setAxis(t.key); setLevel('') }} style={{ cursor: 'pointer' }}>
                <i className={`${t.icon} me-1`} />{t.label}
                {Number(pendingByAxis[t.key] || 0) > 0 && (
                  <Badge bg={axis === t.key ? 'light' : 'danger'} text={axis === t.key ? 'dark' : undefined} pill className="ms-2">
                    {pendingByAxis[t.key]}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {/* บันไดขั้นตอนดูแลของแท็บนี้ */}
        <Card className="card-one mb-3">
          <Card.Body className="py-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="fw-semibold"><i className="ri-route-line me-1 text-primary" />ขั้นตอนดูแล — {axisMeta.label}</span>
              <span className="text-muted fs-xs">เกณฑ์: {axisMeta.criteria}</span>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {steps.map((s, i) => (
                <div key={s.key} className="d-flex align-items-center gap-2">
                  {i > 0 && <i className="ri-arrow-right-line text-muted d-none d-md-inline" />}
                  <div className="border rounded px-2 py-1 d-flex align-items-center gap-2" style={{ background: 'var(--bs-tertiary-bg, rgba(0,0,0,.02))' }}>
                    <LevelBadge level={s.level} />
                    <Badge bg={WHO_META[s.who]?.bg || 'secondary'} className="fs-xs">{WHO_META[s.who]?.label || s.who}</Badge>
                    <span className="fs-sm">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="fs-xs text-muted mt-2">
              นักเรียนระดับสูงต้องผ่านขั้นตอนของระดับต่ำกว่าด้วย เช่น เสี่ยงสูงต้องมีครบทุกช่อง จึงนับว่า "ครบ"
            </div>
            <div className="fs-xs text-muted mt-1">
              ใต้ช่องที่ติ๊กแล้วจะแสดงตัวเลขตอนติ๊ก → ตอนนี้ เพื่อดูความคืบหน้าหลังดำเนินการ:
              <span className="text-success ms-1"><i className="ri-arrow-up-line fw-bold" />ดีขึ้น</span>
              <span className="text-danger ms-2"><i className="ri-arrow-down-line fw-bold" />แย่ลง</span>
              <span className="ms-2"><i className="ri-subtract-line fw-bold" />เท่าเดิม</span>
              <span className="ms-2">(ช่องที่ติ๊กไว้ก่อนมีระบบนี้จะไม่มีตัวเลขเทียบ)</span>
            </div>
            <div className="fs-xs text-muted mt-1">
              <i className="ri-lock-line me-1" />
              ช่องที่ตัวเลขขยับไปจากตอนติ๊กแล้วจะล็อกไม่ให้ยกเลิกติ๊ก — เพราะยกเลิกแล้วติ๊กใหม่
              ระบบจะเก็บตัวเลขของวันที่ติ๊กใหม่แทน ตัวเลขตั้งต้นที่ใช้วัดความคืบหน้าจะหายไป
            </div>
          </Card.Body>
        </Card>

        {/* การ์ดสรุป (กดการ์ดระดับเพื่อกรอง) */}
        <Row className="g-3 mb-3">
          {summaryCards.map(c => (
            <Col key={c.key} xs={6} md={4} xl>
              <Card
                className={`card-one h-100${c.filter && level === c.filter ? ' border-primary' : ''}`}
                style={c.filter ? { cursor: 'pointer' } : undefined}
                onClick={c.filter ? () => setLevel(level === c.filter ? '' : c.filter) : undefined}
              >
                <Card.Body className="d-flex align-items-center gap-3 py-3">
                  <div className={`d-flex align-items-center justify-content-center rounded bg-${c.bg} text-white flex-shrink-0`} style={{ width: 44, height: 44 }}>
                    <i className={`${c.icon} fs-4`} />
                  </div>
                  <div>
                    <h3 className="mb-0">{c.value ?? 0}</h3>
                    <span className="text-muted fs-sm">{c.label}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ตัวกรอง */}
        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col xs={6} md={2}>
                <Form.Label className="fs-sm mb-1">เพศ</Form.Label>
                <Form.Select size="sm" value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  <option value="m">ชาย</option>
                  <option value="f">หญิง</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="fs-sm mb-1">ชั้น</Form.Label>
                <Form.Select size="sm" value={classId} onChange={e => setClassId(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  {classList.map(c => <option key={c.c_id} value={c.c_id}>{c.c_class || `ม.${c.c_id}`}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="fs-sm mb-1">ห้องเรียน</Form.Label>
                <Form.Select size="sm" value={roomId} onChange={e => setRoomId(e.target.value)} disabled={!classId}>
                  <option value="">ทั้งหมด</option>
                  {filteredRooms.map(r => <option key={r.r_id} value={r.r_id}>{r.c_id}/{r.r_no}</option>)}
                </Form.Select>
              </Col>
              {axis === 'academic' && (
                <Col xs={6} md={2}>
                  <Form.Label className="fs-sm mb-1">ภาคเรียน</Form.Label>
                  <Form.Select size="sm" value={semester} onChange={e => setSemester(e.target.value)}>
                    <option value="all">ทั้งปี</option>
                    <option value="1">ภาคเรียนที่ 1</option>
                    <option value="2">ภาคเรียนที่ 2</option>
                  </Form.Select>
                </Col>
              )}
              <Col xs={12} md={axis === 'academic' ? 2 : 3}>
                <Form.Label className="fs-sm mb-1">ระดับ</Form.Label>
                <div className="btn-group btn-group-sm w-100">
                  {levelOptions.map(([v, label]) => (
                    <button key={v || 'all'} type="button" className={`btn btn${level === v ? '' : '-outline'}-primary`} onClick={() => setLevel(v)}>{label}</button>
                  ))}
                </div>
              </Col>
              <Col xs={12} md={axis === 'academic' ? 2 : 3}>
                <Form.Label className="fs-sm mb-1">สถานะ</Form.Label>
                <div className="btn-group btn-group-sm w-100">
                  {[['pending', 'ยังไม่ครบ'], ['complete', 'ครบแล้ว'], ['', 'ทั้งหมด']].map(([v, label]) => (
                    <button key={v || 'all'} type="button" className={`btn btn${status === v ? '' : '-outline'}-primary`} onClick={() => setStatus(v)}>{label}</button>
                  ))}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* รายการนักเรียน */}
        <Card className="card-one">
          <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">รายชื่อนักเรียน — {axisMeta.label}</Card.Title>
              <Badge bg="light" text="dark" className="fs-sm">{rows.length} คน</Badge>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="fs-xs text-muted d-none d-lg-inline">กดชื่อเพื่อดูรายละเอียดนักเรียน</span>
              <Button size="sm" variant="outline-primary" disabled={!rows.length} onClick={printChecklist}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {rows.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="ri-shield-check-line fs-2 d-block mb-2" />
                ไม่มีนักเรียนที่เข้าเกณฑ์ตามตัวกรองนี้
              </div>
            ) : (
              <>
                {/* จอใหญ่: ตาราง */}
                <div className="d-none d-md-block" style={{ maxHeight: 640, overflowY: 'auto' }}>
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead className="table-secondary sticky-top">
                      <tr>
                        <th className="text-center" style={{ width: 44 }}>#</th>
                        <th style={{ minWidth: 200 }}>นักเรียน</th>
                        <th className="text-center" style={{ width: 70 }}>ห้องสามัญ</th>
                        <th className="text-center" style={{ width: 70 }}>ห้องศาสนา</th>
                        <th style={{ minWidth: 190 }}>ระดับ / ข้อมูล</th>
                        <th style={{ minWidth: 140 }}>เบอร์ผู้ปกครอง</th>
                        {steps.map(s => (
                          <th key={s.key} className="text-center" style={{ minWidth: 120 }}>
                            <div>{s.short}</div>
                            <div className="fw-normal fs-xs"><LevelBadge level={s.level}>{STEP_HEAD_LABEL[s.level]}</LevelBadge> <span className="text-muted">{WHO_META[s.who]?.label}</span></div>
                          </th>
                        ))}
                        <th className="text-center" style={{ width: 100 }}>สถานะ</th>
                        {!readOnly && <th className="text-center" style={{ width: 76 }}>สัญญา</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={r.st_id} className={r.complete ? 'table-success' : undefined}>
                          <td className="text-center text-muted">{idx + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => navigate(`/academic/student-tracking/${r.st_id}?yearId=${yearId}`)}>
                              <StudentAvatar photo={r.st_photo} name={r.st_name} />
                              <div>
                                <div className="fw-semibold">{r.st_name}</div>
                                <div className="fs-xs text-muted">{r.st_idnumber}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-center fs-sm">{r.class_room}</td>
                          <td className="text-center fs-sm">{r.religious_room || '—'}</td>
                          <td>
                            <LevelBadge level={r.axis?.level} />
                            <div className="fs-xs text-muted mt-1">{axisDetail(axis, r.axis)}</div>
                          </td>
                          <td><ContactPhone contact={r.contact} level={r.axis?.level} /></td>
                          {steps.map(s => (
                            <td key={s.key} className="text-center">
                              <StepCheck
                                axisKey={axis}
                                step={s}
                                action={r.actions?.[s.key]}
                                required={r.required.includes(s.key)}
                                allowed={(r.canTick || []).includes(s.key)}
                                busy={busyKey === `${r.st_id}:${s.key}`}
                                onToggle={(done) => toggleStep(r, s, done)}
                                onNote={() => editNote(r, s)}
                              />
                            </td>
                          ))}
                          <td className="text-center">
                            {r.complete
                              ? <Badge bg="success" className="fs-xs"><i className="ri-check-line me-1" />ครบ</Badge>
                              : <Badge bg="light" text="dark" className="fs-xs border">ขาด {r.missing.length}/{r.required.length}</Badge>}
                          </td>
                          {!readOnly && (
                            <td className="text-center">
                              <OverlayTrigger placement="top" overlay={<Tooltip>พิมพ์หนังสือสัญญา (A4)</Tooltip>}>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="px-2 py-0"
                                  onClick={(e) => { e.stopPropagation(); openContract(r) }}
                                >
                                  <i className="ri-printer-line" />
                                </Button>
                              </OverlayTrigger>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* มือถือ: การ์ด */}
                <div className="d-md-none p-3 d-flex flex-column gap-3">
                  {rows.map((r, idx) => (
                    <div key={r.st_id} className={`border rounded p-3${r.complete ? ' border-success' : ''}`}>
                      <div className="d-flex align-items-start gap-2" onClick={() => navigate(`/academic/student-tracking/${r.st_id}?yearId=${yearId}`)}>
                        <StudentAvatar photo={r.st_photo} name={r.st_name} size={40} />
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="fw-semibold text-truncate">{r.st_name}</div>
                            <LevelBadge level={r.axis?.level} />
                          </div>
                          <div className="fs-xs text-muted">
                            #{idx + 1} · {r.st_idnumber} · สามัญ {r.class_room}
                            {r.religious_room ? ` · ศาสนา ${r.religious_room}` : ''}
                          </div>
                          <div className="fs-xs text-muted">{axisDetail(axis, r.axis)}</div>
                        </div>
                      </div>
                      {r.contact?.phone && (
                        <div className="mt-2">
                          <ContactPhone contact={r.contact} level={r.axis?.level} />
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-top d-flex flex-column gap-2">
                        {steps.map(s => (
                          <div key={s.key} className="d-flex align-items-center justify-content-between gap-2">
                            <div className="fs-sm">
                              <LevelBadge level={s.level}>{STEP_HEAD_LABEL[s.level]}</LevelBadge> <span className="ms-1">{s.short}</span>
                              <div className="fs-xs text-muted">{WHO_META[s.who]?.label}</div>
                            </div>
                            <StepCheck
                              axisKey={axis}
                              step={s}
                              action={r.actions?.[s.key]}
                              required={r.required.includes(s.key)}
                              allowed={(r.canTick || []).includes(s.key)}
                              busy={busyKey === `${r.st_id}:${s.key}`}
                              onToggle={(done) => toggleStep(r, s, done)}
                              onNote={() => editNote(r, s)}
                            />
                          </div>
                        ))}
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          {!readOnly ? (
                            <Button variant="outline-secondary" size="sm" onClick={() => openContract(r)}>
                              <i className="ri-printer-line me-1" />พิมพ์สัญญา
                            </Button>
                          ) : <span />}
                          {r.complete
                            ? <Badge bg="success" className="fs-xs"><i className="ri-check-line me-1" />ครบ</Badge>
                            : <Badge bg="light" text="dark" className="fs-xs border">ขาด {r.missing.length}/{r.required.length}</Badge>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* ตัวอย่างหนังสือสัญญาก่อนพิมพ์ — react-to-print พิมพ์เฉพาะกล่องนี้ */}
      <Modal show={!!contract} onHide={() => setContract(null)} size="xl" fullscreen="lg-down" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            <i className="ri-file-text-line me-2 text-primary" />
            หนังสือสัญญา{contract?.st_name ? ` — ${contract.st_name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#6c757d' }}>
          <div style={{ overflowX: 'auto' }}>
            <div ref={contractRef} style={{ background: '#fff', width: '21cm', margin: '0 auto' }}>
              {contract && <CareContractDoc info={contract} />}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="text-muted fs-xs me-auto d-none d-md-inline">
            ช่องที่ระบบไม่มีข้อมูลจะพิมพ์เป็นเส้นประให้เขียนด้วยมือ
          </span>
          <Button variant="light" onClick={() => setContract(null)}>ปิด</Button>
          <Button variant="primary" onClick={printContract}>
            <i className="ri-printer-line me-1" />พิมพ์ A4
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </React.Fragment>
  )
}
